import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { theme, getStatusMeta, ACTIVE_JOB_STATUSES } from "./theme";

// Color grouped by file family — label always shows the file's real extension.
const EXTENSION_COLOR = {
  pdf: { color: theme.danger, background: theme.dangerLight },
  doc: { color: theme.info, background: theme.infoLight },
  docx: { color: theme.info, background: theme.infoLight },
  xls: { color: theme.success, background: theme.successLight },
  xlsx: { color: theme.success, background: theme.successLight },
  png: { color: theme.primaryHover, background: theme.primaryLight },
  jpg: { color: theme.primaryHover, background: theme.primaryLight },
  jpeg: { color: theme.primaryHover, background: theme.primaryLight },
  gif: { color: theme.primaryHover, background: theme.primaryLight },
  webp: { color: theme.primaryHover, background: theme.primaryLight },
  svg: { color: theme.primaryHover, background: theme.primaryLight },
};

// Display names (doc.title) are often extension-less user-chosen labels (e.g.
// "Permit parking_1785176831223") — the real extension lives on the uploaded
// file's URL (doc.link), so that's checked first and title is only a fallback.
function extractExtension(value) {
  if (!value) return null;
  const withoutQuery = value.split(/[?#]/)[0];
  const last = withoutQuery.split(".").pop();
  if (!last || last === withoutQuery || !/^[a-zA-Z0-9]{1,5}$/.test(last)) return null;
  return last.toLowerCase();
}

function getExtensionMeta(doc) {
  const ext = extractExtension(doc?.link) || extractExtension(doc?.title);
  if (!ext) {
    return { label: "FILE", color: theme.textFaint, background: theme.background };
  }
  const colorMeta = EXTENSION_COLOR[ext] || { color: theme.textFaint, background: theme.background };
  return { label: ext.toUpperCase(), ...colorMeta };
}

// Memoized: the per-printer history renders every job in the range (up to ~1.7K
// cards today), and every live socket event hands the list a new array. With
// shallow prop equality only the card whose `doc` (or `printer`) object actually
// changed re-renders. That is also why this takes the single `printer` rather
// than the whole `printers` map — the map is a new object on every fleet-wide
// printer-status event, the individual printer object is not.
const JobCard = React.memo(function JobCard({
  doc,
  printer,
  renderColor,
  jobSelected,
  onJobClick,
  selectable = false,
  checked = false,
  onToggleCheck,
  onOpenPrinter,
}) {
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (renderColor) {
      setTimeout(() => setFlash(true), 100);
      setTimeout(() => setFlash(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leadingZero = (num) => `0${num}`.slice(-2);

  const unixDate = doc.unix instanceof Date ? doc.unix : new Date(doc.unix || Date.now());

  const _renderDate = () => {
    return `${leadingZero(unixDate.getDate())}/${leadingZero(
      unixDate.getMonth() + 1
    )}/${unixDate.getFullYear()}`;
  };
  const _renderTime = () => {
    return `${leadingZero(unixDate.getHours())}:${leadingZero(
      unixDate.getMinutes()
    )}:${leadingZero(unixDate.getSeconds())}`;
  };

  const doneStatus = ["printing", "in progress"];

  // cancelReasonTitle (the CS-selected finance reason for a dashboard cancel) takes
  // priority over failReasonTitle (the physical RPI reason, e.g. paper jam) — once a
  // job is canceled, why it was canceled matters more than why it was paused before.
  const reasonText = doc.cancelReasonTitle || doc.failReasonTitle || null;

  const statusLine =
    doneStatus.includes(doc.status) && printer && printer.status !== "OK"
      ? `Status: ${printer.status}`
      : reasonText
      ? ` — ${reasonText}`
      : "";

  const isSelected = jobSelected?.id === doc.id;
  const meta = getStatusMeta(doc.status);
  const extMeta = getExtensionMeta(doc);

  return (
    <Wrapper
      isSelected={isSelected}
      flash={flash}
      onClick={() => onJobClick?.(isSelected ? null : doc)}
    >
      {selectable ? (
        <CheckboxCell onClick={(e) => e.stopPropagation()}>
          {ACTIVE_JOB_STATUSES.includes(doc.status) ? (
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onToggleCheck?.(doc.id, e.target.checked)}
            />
          ) : null}
        </CheckboxCell>
      ) : null}
      <ExtBadge extColor={extMeta.color} extBackground={extMeta.background}>
        {extMeta.label}
      </ExtBadge>
      <TitleCell>
        <Title>{doc.title}</Title>
        {doc.userEmail ? <Subtitle>{doc.userEmail}</Subtitle> : null}
      </TitleCell>
      <RoomCell>
        {onOpenPrinter && doc.printer_id ? (
          // A span, not a button — the whole card Wrapper is already a <button>
          // and nested buttons are invalid HTML (browsers break the outer one).
          <RoomLink
            role="link"
            tabIndex={0}
            title="Open printer page"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPrinter(
                printer || { printer_id: doc.printer_id, room_name: doc.roomName }
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onOpenPrinter(
                  printer || { printer_id: doc.printer_id, room_name: doc.roomName }
                );
              }
            }}
          >
            {doc.roomName || printer?.room_name}
          </RoomLink>
        ) : (
          doc.roomName || printer?.room_name
        )}
      </RoomCell>
      <ReasonCell>{statusLine}</ReasonCell>
      <DateTimeCell>
        <DateLabel>{_renderDate()}</DateLabel>
        <Time>{_renderTime()}</Time>
      </DateTimeCell>
      <StatusCell>
        <StatusBadge badgeColor={meta.color} badgeBackground={meta.background}>
          <StatusDot dotColor={meta.dot} pulse={doc.status === "printing"} />
          {meta.label}
        </StatusBadge>
        {doc.status === "printing" ? (
          <ProgressTrack>
            <ProgressFill />
          </ProgressTrack>
        ) : null}
      </StatusCell>
    </Wrapper>
  );
});

export { JobCard };

const Wrapper = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
  border: 1px solid ${({ isSelected }) => (isSelected ? theme.primary : theme.border)};
  border-radius: ${theme.radiusSm};
  outline: none;
  background-color: ${({ flash, isSelected }) =>
    flash ? theme.warningLight : isSelected ? theme.primaryLight : theme.surface};
  padding: 10px 16px;
  cursor: pointer;
  font-family: ${theme.fontFamily};
  text-align: start;
  transition: background-color 200ms ease, border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    box-shadow: ${theme.shadow};
    border-color: ${theme.borderStrong};
  }
`;

const CheckboxCell = styled.div`
  flex: 0 0 auto;
  padding-right: 12px;
  display: flex;
  align-items: center;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: ${theme.primary};
  }
`;

const ExtBadge = styled.div`
  flex: 0 0 auto;
  margin-right: 12px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: ${theme.radiusSm};
  color: ${({ extColor }) => extColor};
  background: ${({ extBackground }) => extBackground};
`;

const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 16px;
`;

const Title = styled.div`
  width: 100%;
  color: ${theme.text};
  font-size: 13px;
  font-weight: 500;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subtitle = styled.div`
  width: 100%;
  color: ${theme.textFaint};
  font-size: 11px;
  text-align: start;
`;

const RoomCell = styled.div`
  flex: 0 0 auto;
  padding-right: 16px;
  color: ${theme.textMuted};
  font-size: 13px;
  white-space: nowrap;
`;

const RoomLink = styled.span`
  color: ${theme.primary};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${theme.primaryHover};
    text-decoration: underline;
    outline: none;
  }
`;

const ReasonCell = styled.div`
  flex: 0 0 auto;
  padding-right: 16px;
  color: ${theme.danger};
  font-size: 12px;
  white-space: nowrap;
`;

const DateTimeCell = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 16px;
  min-width: 76px;
`;

const DateLabel = styled.div`
  white-space: nowrap;
  color: ${theme.text};
  font-size: 13px;
`;

const Time = styled.div`
  white-space: nowrap;
  color: ${theme.textFaint};
  font-size: 11px;
`;

const StatusCell = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 96px;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ badgeColor }) => badgeColor};
  background: ${({ badgeBackground }) => badgeBackground};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: ${theme.radiusPill};
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ dotColor }) => dotColor};
  flex-shrink: 0;
  ${({ pulse }) =>
    pulse
      ? `animation: dotPulse 1.2s ease-in-out infinite;
         @keyframes dotPulse {
           0%, 100% { opacity: 1; }
           50% { opacity: 0.35; }
         }`
      : ""}
`;

const ProgressTrack = styled.div`
  width: 72px;
  height: 3px;
  border-radius: ${theme.radiusPill};
  background: ${theme.border};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: 40%;
  height: 100%;
  border-radius: ${theme.radiusPill};
  background: ${theme.primary};
  animation: progressSlide 1.4s ease-in-out infinite;

  @keyframes progressSlide {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(250%); }
  }
`;
